<template>
  <v-row
    v-if="brand"
    class="reward-container"
  >
    <v-col
      cols="12"
      lg="8"
      class="reward-details"
    >
      <div class="d-flex justify-space-between align-center">
        <h2 class="reward-title">
          {{ brand.displayName }}
        </h2>
        <tc-v-btn
          class="button-reward-close"
          icon
          tabindex="0"
          :aria-label="$t('close-reward-details-button')"
          role="button"
          @click="closeRewardDetailView"
          @keyup.enter="closeRewardDetailView"
        >
          <v-icon color="secondary">
            mdi-close-thick
          </v-icon>
        </tc-v-btn>
      </div>
      <div class="reward-description mt-6 d-none d-sm-block">
        <img
          :alt="brand.displayName"
          :src="brand.imageUrls['278w-326ppi']"
          class="img-reward-detail img-responsive"
        >
        <span v-html="brand.description" />
      </div>
      <div class="reward-description mt-6 d-block d-sm-none">
        <img
          :alt="brand.displayName"
          :src="brand.imageUrls['278w-326ppi']"
          class="img-reward-detail img-responsive"
        >
        <div
          v-if="showMoreDetails"
          id="reward-description-text"
          v-html="brand.description"
        />
        <tc-v-btn
          id="view-details"
          tabindex="0"
          color="primary"
          text
          @click="handleViewMoreDetailsClick"
          @keyup.enter="handleViewMoreDetailsClick"
        >
          <span class="text-capitalize">
            {{ viewDetailsMessage }}
          </span>
        </tc-v-btn>
      </div>
      <div
        class="reward-disclaimer mt-6 d-none d-sm-block"
        v-html="brand.disclaimer"
      />
    </v-col>
    <v-col
      cols="12"
      lg="4"
      class="text-center select-amount"
    >
      <div class="d-flex align-center">
        <h2 class="d-none d-sm-block selection-title ma-0 flex-grow-1">
          {{ $t('reward-select-amount') }}
        </h2>
        <tc-v-btn
          class="d-none d-md-inline-block"
          icon
          tabindex="0"
          :aria-label="$t('close-reward-details-button')"
          role="button"
          @click="closeRewardDetailView"
          @keyup.enter="closeRewardDetailView"
        >
          <v-icon color="secondary">
            mdi-close-thick
          </v-icon>
        </tc-v-btn>
      </div>
      <form
        id="container-reward-amount"
        @submit.prevent
      >
        <item-selector
          class="mt-5"
          :brand="brand"
          :previous-amount="previousAmount"
          :previous-utid="previousUtid"
          :previous-fulfillment-type="fulfillmentType"
          @change="selectedItemAndAmount = $event"
        />
      </form>

      <direct-payment-button
        v-if="isDirectPayment"
        :payment-type="directPaymentType"
        :is-disabled="disableCartAndCheckoutBtns"
        @click="initiateDirectPayment"
        @keyup.enter.prevent="initiateDirectPayment"
      />

      <checkout-buttons
        v-else
        :is-disabled="disableCartAndCheckoutBtns"
        v-on="checkoutButtonListeners"
      />

      <div
        class="reward-disclaimer mt-6 d-block d-sm-none text-left"
        v-html="brand.disclaimer"
      />
      <tc-v-btn
        id="continue-shopping-btn"
        tabindex="0"
        color="accent"
        text
        block
        @click="continueShopping"
        @keyup.enter="continueShopping"
      >
        <span class="text-capitalize">
          {{ $t('reward-back-to-catalog') }}
        </span>
      </tc-v-btn>
    </v-col>
  </v-row>
</template>

<script>
import ItemSelector from '@/components/reward/details/ItemSelector'
import { mapActions, mapGetters } from 'vuex'
import * as actionTypes from '@/store/action-types'
import {
  GET_IS_EXPIRED,
  GET_IS_LOCKED,
  GET_IS_REDEEMED
} from '@/store/get-types'
import CartItem from '@/store/classes/cart-item'
import { ItemFeeMixin } from '@/components/shared/mixins/item-fee-mixin'
import {
  ADD_TO_CART_CLICKED,
  INSTANT_CHECKOUT_CLICKED
} from '@/plugins/segment/SegmentConstants'
import { CATALOG_ROUTE } from '@/router/routes/route-names'
import CheckoutButtons from '@/components/reward/details/CheckoutButtons'
import { checkoutButtonEvents } from '@/components/reward/details/reward-details-constants'
import { PAYMENT_REWARD_TYPES } from '@/components/shared/item/reward-type-constants'
import DirectPaymentButton from '@/components/reward/details/DirectPaymentButton'
import {
  getDirectPaymentRouteForDirectPaymentType,
  getDirectPaymentTypeFromItemType
} from '@/components/direct-payments/direct-payment-constants'
import { FulfillmentType } from '@/cowboy/EnumTypes'

const HIDE_CART_DELAY_IN_MILLISECONDS = 4000
const MOBILE_VIEW_PORT_WIDTH_IN_PX = 767

export default {
  name: 'RewardDetailView',
  components: {
    CheckoutButtons,
    DirectPaymentButton,
    ItemSelector
  },
  mixins: [ItemFeeMixin],
  props: {
    brand: {
      type: Object,
      required: true
    },
    trianglePosition: {
      default: undefined,
      type: Number,
      required: false
    },
    row: {
      default: undefined,
      type: Number,
      required: false
    },
    previousAmount: {
      type: Number,
      required: false,
      default: undefined
    },
    previousUtid: {
      type: String,
      required: false,
      default: undefined
    },
    fulfillmentType: {
      type: String,
      required: false,
      default: FulfillmentType.DIGITAL
    }
  },
  data () {
    return {
      showMoreDetails: false,
      addingToCart: false,
      selectedItemAndAmount: {
        item: {},
        amount: null
      }
    }
  },
  computed: {
    ...mapGetters({
      isExpired: GET_IS_EXPIRED,
      isRedeemed: GET_IS_REDEEMED,
      isLocked: GET_IS_LOCKED
    }),
    brandItems () {
      const { items } = this.brand || []
      return items
    },
    checkoutButtonListeners () {
      return {
        [checkoutButtonEvents.CHECKOUT]: () => this.addToCart(),
        [checkoutButtonEvents.INSTANT_CHECKOUT]: () =>
          this.addToCart(checkoutButtonEvents.INSTANT_CHECKOUT)
      }
    },
    directPaymentRoute () {
      return getDirectPaymentRouteForDirectPaymentType(this.directPaymentType)
    },
    directPaymentType () {
      const [firstItem] = this.brandItems
      const { rewardType } = firstItem || {}
      const { description } = rewardType || {}
      return getDirectPaymentTypeFromItemType(description)
    },
    isDirectPayment () {
      return this.brandItems.some(
        item =>
          item.rewardType &&
          PAYMENT_REWARD_TYPES.includes(item.rewardType.description)
      )
    },
    viewDetailsMessage () {
      return this.showMoreDetails
        ? this.$t('reward-view-less-details')
        : this.$t('reward-view-details')
    },
    disableCartAndCheckoutBtns () {
      return (
        this.errors.any() ||
        this.addingToCart ||
        this.isRedeemed ||
        this.isExpired ||
        this.isLocked
      )
    }
  },
  methods: {
    ...mapActions({
      updateBalance: actionTypes.UPDATE_REWARD_LINK_BALANCE,
      addCartItem: actionTypes.ADD_CART_ITEM,
      setShowCart: actionTypes.SET_SHOW_CART,
      resetCart: actionTypes.RESET_CART,
      setDirectPaymentAmount: actionTypes.SET_DIRECT_PAYMENT_AMOUNT,
      setDirectPaymentType: actionTypes.SET_DIRECT_PAYMENT_TYPE,
      setDirectPaymentUtid: actionTypes.SET_DIRECT_PAYMENT_UTID
    }),
    async addToCart (addToCartOption) {
      if (this.addingToCart) {
        return
      }
      const isAddingToCartValid = await this.$validator.validateAll()
      if (isAddingToCartValid) {
        this.addSelectedItemAndAmountToCart(addToCartOption)
      }
    },
    addSelectedItemAndAmountToCart (addToCartOption) {
      this.addingToCart = true
      const {
        brand,
        selectedItemAndAmount: { item, amount },
        row,
        trianglePosition
      } = this
      const {
        utid,
        adjustment,
        currencyCode,
        valueType,
        fulfillmentType
      } = item
      const {
        displayName,
        imageUrls: { '278w-326ppi': imageUrl }
      } = brand
      const floatAmount = +parseFloat(amount).toFixed(2)
      const itemFee = this.getFormattedItemFee(floatAmount, adjustment)
      const itemEditData = {
        brand,
        row,
        trianglePosition,
        previousAmount: floatAmount,
        previousUtid: utid,
        fulfillmentType
      }

      const cartItem = new CartItem(
        utid,
        '',
        floatAmount,
        itemFee,
        brand,
        currencyCode,
        imageUrl,
        displayName,
        itemEditData,
        valueType,
        fulfillmentType
      )
      this.addCartItem(cartItem)
      this.updateBalance(
        -this.getAmountWithAppliedFeeIfItExists(floatAmount, itemFee)
      )
      if (this.isInstantCheckout(addToCartOption)) {
        this.instantCheckout()
      } else {
        this.transitionAfterItemIsAddedToCart()
      }
      this.addingToCart = false
    },
    async initiateDirectPayment () {
      const isValid = await this.$validator.validateAll()

      if (isValid && this.directPaymentRoute) {
        const { amount, item } = this.selectedItemAndAmount || {}
        const { utid } = item || {}

        await this.resetCart()
        await this.setDirectPaymentAmount(amount)
        await this.setDirectPaymentType(this.directPaymentType)
        await this.setDirectPaymentUtid(utid)

        await this.$router.push({ name: this.directPaymentRoute })
      }
    },
    isInstantCheckout (addToCartOption) {
      return addToCartOption === checkoutButtonEvents.INSTANT_CHECKOUT
    },
    instantCheckout () {
      this.$segment.track(INSTANT_CHECKOUT_CLICKED)
      this.$router.push({ name: 'Checkout' })
    },
    transitionAfterItemIsAddedToCart () {
      this.$segment.track(ADD_TO_CART_CLICKED)
      const isInMobileView = window.innerWidth <= MOBILE_VIEW_PORT_WIDTH_IN_PX
      isInMobileView
        ? this.routeDirectlyToTheCart()
        : this.showCartNotificationAndThenHideItAfterDelay()
    },
    routeDirectlyToTheCart () {
      this.$router.push({ name: 'Cart' })
    },
    showCartNotificationAndThenHideItAfterDelay () {
      this.setShowCart({ isShown: true, fadeOut: true })
      setTimeout(() => {
        this.setShowCart({ isShown: false, fadeOut: true })
      }, HIDE_CART_DELAY_IN_MILLISECONDS)
      this.closeRewardDetailView()
    },
    closeRewardDetailView () {
      const message = this.$t('brand-reward-dialog-closed')
      this.$emit('close', true)
      this.$announcer.set(message)
    },
    handleViewMoreDetailsClick () {
      this.showMoreDetails = !this.showMoreDetails
    },
    continueShopping () {
      this.$router.push({ name: CATALOG_ROUTE })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/css/variables';

.select-amount {
  padding-top: 20px;

  #continue-shopping-btn {
    display: none;
  }
}

.reward-title {
  margin-bottom: 0;
}

.reward-details {
  background-color: #fff;
  padding: 20px;
  z-index: 1;

  h2 {
    color: $tangoTextPrimary;
  }

  .img-reward-detail {
    display: inline-block;
    object-fit: cover;
    border-radius: 8px;
  }
}

.reward-description {
  font-size: $font-size-md;
  text-align: left;
  color: $tangoTextPrimary;

  button {
    font-size: $font-size-md;
    color: $tangoTextPrimary;
    &#view-details {
      color: $tangoTextBlue;
    }
  }
}

.reward-disclaimer {
  margin-bottom: 38px;
  font-size: $font-size-sm;
  color: $tangoTextPrimary;
  word-break: break-word;
  p {
    font-size: $font-size-sm;
  }
}
.active {
  border: 2px solid $tangoBlue;
}

.delivery {
  background-color: $tangoWhite;
  text-align: left;
  margin-top: 10px;
  padding: 10px;

  .label-text {
    margin-bottom: 0;
  }
}

.reward-amount {
  height: 52px;
  width: 100%;
  background-color: #fff;
  border: 1px solid $tangoBlueGrayDk;
  line-height: 52px;
  font-size: $font-size-lg;
  color: $tangoTextPrimary;

  #input-reward-amount {
    border: 0;
    border-left: 1px solid $tangoBlueGrayDk;
    height: 100%;
    width: 80%;
    float: right;

    &[type='number'] {
      -moz-appearance: textfield;
      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
    }
  }
}

#fee-message {
  color: $tangoDangerRed;
  font-size: $font-size-md;
}

.reward-detail-button {
  margin-top: 25px;
  width: 100%;
  min-width: 0;
  text-transform: none;
}

.reward-details .button-reward-close {
  display: none;
}

#reward-amount-fixed-error {
  margin-top: 20px;
}

.selection-title {
  color: $tangoTextSecondary;
}

@media screen and (max-width: $breakpoint-sm - 1) {
  .select-amount #continue-shopping-btn {
    display: block;
  }
}

@media screen and (max-width: $breakpoint-lg - 1) {
  .select-amount .button-reward-close {
    display: none;
  }
}

@media screen and (min-width: $breakpoint-sm) and (max-width: $breakpoint-lg - 1) {
  .reward-details .button-reward-close {
    display: initial;
  }
  .reward-details {
    margin-top: 9px;
  }
}

@media screen and (max-width: $breakpoint-sm - 1) {
  .reward-description {
    text-align: center;
  }

  .reward-details {
    .img-reward-detail {
      width: 320px;
      margin: 0 auto 16px;
    }
  }
}

@media screen and (max-width: $breakpoint-md - 1) {
  .reward-details {
    .img-reward-detail {
      height: 112px;
      width: 180px;
      margin: 16px;
    }
  }

  .delivery {
    border: 1px solid $tangoBlueGrayDk;
  }

  .reward-amount {
    height: 48px;
  }
}

@media screen and (min-width: $breakpoint-md) {
  .reward-details {
    padding-left: 36px;

    h2 {
      font-size: $font-size-lg;
      color: $tangoTextPrimary;
      text-align: left;
    }

    .img-reward-detail {
      width: 203px;
      height: 127px;
      margin-right: 36px;
      float: left;
      color: $tangoTextPrimary;
    }
  }

  .reward-description {
    font-size: $font-size-sm;

    &:hover {
      cursor: pointer;
    }
  }
}
.selection-title {
  color: $tangoTextPrimary;
}
</style>
